import type { QueuedMutation } from './offlineQueue';
import type { RouteRoadStretch, RouteSegment } from '../types/surveillanceRoute';

/** Un tramo que aún no ha llegado al servidor (o cuyo cierre no ha llegado). */
export type PendingSegment = RouteSegment & { isPending: true };

/**
 * Prefijo del id sintético de un tramo que SOLO existe en la cola offline.
 * Ese id no vale para ninguna mutation que referencie el tramo (el servidor no
 * lo conoce todavía): quien ofrezca acciones sobre un tramo debe comprobarlo
 * con `hasSyntheticId` antes de mandar su id a ningún sitio.
 */
export const PENDING_SEGMENT_ID_PREFIX = 'pending-';

/** ¿Este tramo existe solo en la cola local (id que el servidor no conoce)? */
export function hasSyntheticId(segment: { id: string } | null | undefined): boolean {
  return segment?.id?.startsWith(PENDING_SEGMENT_ID_PREFIX) === true;
}

const CREATE_OP = 'CreateRouteSegment';
const FINISH_OP = 'FinishRouteSegment';

interface CreateInput {
  surveillanceRouteId?: string;
  direction?: string;
  directionName?: string;
  lane?: string;
  pkInitKm?: number;
  pkInitMeter?: number;
  initLatitude?: number;
  initLongitude?: number;
  /**
   * Hora de inicio capturada en el CLIENTE (front #117 / back #72). El tramo
   * real acaba con EXACTAMENTE esta hora, así que sirve de dos cosas: que la
   * fila optimista no salte de hora al sincronizar, y como clave FIABLE de
   * confirmación (ver `mergePendingSegments`).
   */
  registerInitDate?: string;
}

interface FinishInput {
  id?: string;
  endDate?: string;
  pkEndKm?: number;
  pkEndMeter?: number;
  endLatitude?: number;
  endLongitude?: number;
  pauseCause?: string;
  pauseCauseDetail?: string;
  lane?: string;
  /**
   * Por qué carreteras pasó el tramo (multi-carretera 2026-08-17). Lo resuelve
   * el modal de cierre ANTES de encolar, así que sin cobertura ya viaja aquí:
   * si no se copia a la fila, la tabla enseña solo la vía donde empezó y unos
   * km de restar los extremos, que con dos vías no significan nada.
   */
  roadStretches?: RouteRoadStretch[];
}

function inputOf<T>(op: QueuedMutation): T {
  return (op.variables?.input ?? {}) as T;
}

/**
 * Fusiona los tramos que devolvió el loader con los que ESPERAN en la cola
 * offline.
 *
 * Sin esto, iniciar/finalizar/cambiar de sentido sin cobertura no se ve: la
 * mutation queda encolada (devuelve el sentinel `null`, ver
 * `useSurveillanceRoutes`) pero la página lee el SNAPSHOT del loader, que sin
 * red sirve la caché anterior.
 *
 * DECISIÓN: los pendientes se DERIVAN de la cola en vez de guardarse en un
 * almacén propio. La cola ya persiste `operationName` + `variables` íntegras,
 * así que no hay un segundo estado que rellenar al encolar y vaciar al
 * confirmar — que es justo donde estos mecanismos se pudren (el tramo fantasma
 * que no se va nunca). Cuando el replay borra la operación, el pendiente
 * desaparece por construcción.
 *
 * Precedencia: **el servidor siempre gana**. Un cierre encolado sobre un tramo
 * que el servidor ya devuelve cerrado se ignora — el replay lo resolverá de
 * forma idempotente y así un pendiente rezagado nunca pisa el dato bueno.
 */
export function mergePendingSegments(
  serverSegments: RouteSegment[],
  queue: QueuedMutation[],
  routeId: string | undefined
): RouteSegment[] {
  if (!routeId || queue.length === 0) return serverSegments;

  const relevant = queue.filter(
    (op) => op.operationName === CREATE_OP || op.operationName === FINISH_OP
  );
  if (relevant.length === 0) return serverSegments;

  // ── Cierres encolados: se aplican sobre el tramo del servidor ──────────
  const finishes = new Map<string, { op: QueuedMutation; input: FinishInput }>();
  relevant
    .filter((op) => op.operationName === FINISH_OP)
    .forEach((op) => {
      const input = inputOf<FinishInput>(op);
      if (input.id) finishes.set(input.id, { op, input });
    });

  const withFinishes = serverSegments.map((segment) => {
    const finish = finishes.get(segment.id);
    if (!finish || segment.endDate) return segment;
    const { op, input } = finish;
    return {
      ...segment,
      endDate: input.endDate ?? new Date(op.timestamp).toISOString(),
      pkEndKm: input.pkEndKm ?? segment.pkEndKm,
      pkEndMeter: input.pkEndMeter ?? segment.pkEndMeter,
      endLatitude: input.endLatitude ?? segment.endLatitude,
      endLongitude: input.endLongitude ?? segment.endLongitude,
      pauseCause: input.pauseCause ?? segment.pauseCause,
      pauseCauseDetail: input.pauseCauseDetail ?? segment.pauseCauseDetail,
      lane: input.lane ?? segment.lane,
      roadStretches: input.roadStretches ?? segment.roadStretches,
      isPending: true,
    } as PendingSegment;
  });

  // Horas de inicio que el servidor YA tiene. Como `createSegment` sella
  // `registerInitDate` en el cliente, el tramo confirmado vuelve con esa misma
  // hora → es una clave de confirmación exacta, sin heurísticas de proximidad.
  const serverStartDates = new Set(
    serverSegments.map((segment) => segment.startDate)
  );

  // ── Creaciones encoladas: tramos nuevos, activos (sin endDate) ─────────
  const creations: PendingSegment[] = relevant
    .filter((op) => op.operationName === CREATE_OP)
    .map((op) => ({ op, input: inputOf<CreateInput>(op) }))
    .filter(({ input }) => input.surveillanceRouteId === routeId)
    // Ya confirmado por el servidor ⇒ es el MISMO tramo, no uno nuevo. Sin
    // este filtro, la ventana entre "la cola replaya" y "el loader revalida"
    // pintaría el tramo DOS veces (el real y su fantasma).
    .filter(
      ({ input }) =>
        !input.registerInitDate || !serverStartDates.has(input.registerInitDate)
    )
    .map(({ op, input }) => ({
      // El id de la operación en cola hace de id estable del tramo fantasma:
      // sobrevive a los re-render y no puede chocar con un ObjectId real.
      id: `${PENDING_SEGMENT_ID_PREFIX}${op.id}`,
      state: 'in-progress',
      direction: input.direction ?? 'top',
      directionName: input.directionName,
      lane: input.lane,
      pkInitKm: input.pkInitKm ?? 0,
      pkInitMeter: input.pkInitMeter ?? 0,
      pkEndKm: null,
      pkEndMeter: null,
      initLatitude: input.initLatitude,
      initLongitude: input.initLongitude,
      // La del CLIENTE si viene (lo normal desde front #117); el instante de
      // encolado solo como red de seguridad para ops encoladas por una versión
      // anterior que siguieran en la cola tras actualizar la app.
      startDate: input.registerInitDate ?? new Date(op.timestamp).toISOString(),
      endDate: null,
      pendingExternalLink: false,
      // Un tramo recién iniciado no ha cerrado todavía, así que no puede estar
      // esperando su Actuació básica (P15 Fase 2: la marca se pone al CERRAR
      // con causa `action`). Al sincronizar manda el valor real del servidor.
      pendingActionLink: false,
      orderIDs: [],
      isPending: true,
    }));

  // Un tramo creado sin cobertura y CERRADO sin recuperarla: su cierre también
  // está en la cola, apuntando al id sintético. Sin esto el tramo se quedaría
  // corriendo en pantalla para siempre —cronómetro incluido— aunque el operario
  // ya lo hubiera finalizado, y el siguiente no podría empezar.
  const closedPending = creations.map((segment) => {
    const finish = finishes.get(segment.id);
    if (!finish) return segment;
    const { op, input } = finish;
    return {
      ...segment,
      endDate: input.endDate ?? new Date(op.timestamp).toISOString(),
      pkEndKm: input.pkEndKm ?? segment.pkEndKm,
      pkEndMeter: input.pkEndMeter ?? segment.pkEndMeter,
      endLatitude: input.endLatitude ?? segment.endLatitude,
      endLongitude: input.endLongitude ?? segment.endLongitude,
      pauseCause: input.pauseCause ?? segment.pauseCause,
      pauseCauseDetail: input.pauseCauseDetail ?? segment.pauseCauseDetail,
      lane: input.lane ?? segment.lane,
      roadStretches: input.roadStretches ?? segment.roadStretches,
    };
  });

  return [...withFinishes, ...closedPending];
}
